import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Container, Spinner } from "@/components/shared";
import { toast } from "sonner";
import { ArrowLeft, BadgeCheck, Globe, CalendarDays, Instagram, Music2, ExternalLink, Users, ShieldCheck, Stethoscope, Phone, Mail } from "lucide-react";

const CAT_LABELS = {
  psychologie: "🧠 Psychologie", racisme: "✊🏿 Racisme & discriminations", identite: "🌍 Identité culturelle",
  distance: "💻 À distance", juridique: "⚖️ Juridique", histoire: "🏛️ Histoire", education: "👨🏿‍🏫 Éducation",
  jeunesse: "👦 Jeunesse", parents: "👨‍👩‍👧 Parents", auteurs: "📚 Auteurs", createurs: "🎥 Créateurs de contenu",
};
const ICONS = { globe: Globe, calendar: CalendarDays, instagram: Instagram, tiktok: Music2, doctolib: Stethoscope, phone: Phone, mail: Mail };

export default function PartnerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/partners/${id}`).then(({ data }) => setP(data))
      .catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Container><Spinner label="Chargement du professionnel..." /></Container>;
  if (!p) return <Container><p className="text-white/50">Professionnel introuvable.</p></Container>;

  return (
    <Container className="max-w-3xl">
      <button data-testid="partner-back" onClick={() => navigate("/partenaires")}
        className="text-white/50 hover:text-gold text-sm inline-flex items-center gap-2 mb-6"><ArrowLeft size={16} /> Nos professionnels</button>

      {/* 1. Photo */}
      <div className="flex flex-col items-center text-center">
        <div className="w-40 h-40 md:w-48 md:h-48 rounded-3xl overflow-hidden border border-gold/20 shadow-[0_0_40px_-12px_rgba(212,175,55,0.3)]" data-testid="partner-photo">
          {p.photo_url
            ? <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center bg-white/[0.03]"><Users className="text-white/20" size={44} /></div>}
        </div>

        {/* 2. Nom / 3. Fonction */}
        <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-6" data-testid="partner-name">{p.name}</h1>
        <p className="text-white/60 mt-2 max-w-xl">{p.role}</p>

        {/* 4. Badge (uniquement si partenaire) */}
        {p.badge && (
          <div className="inline-flex items-center gap-2 mt-4 rounded-full border border-gold/40 bg-gold/[0.08] px-4 py-1.5" data-testid="partner-badge">
            <BadgeCheck size={15} className="text-gold" />
            <span className="text-gold text-sm font-medium">{p.badge}</span>
          </div>
        )}

        {/* 5. Catégories */}
        <div className="flex flex-wrap justify-center gap-2 mt-5" data-testid="partner-categories">
          {(p.categories || []).map((c) => (
            <span key={c} className="text-xs rounded-full bg-white/[0.05] border border-white/10 px-2.5 py-1 text-white/70">{CAT_LABELS[c] || c}</span>
          ))}
        </div>
      </div>

      {/* 6. Présentation */}
      {p.presentation && (
        <section className="mt-10">
          <h2 className="font-display text-xl mb-3">Présentation</h2>
          {p.presentation.split("\n\n").map((para, i) => (
            <p key={i} className="text-white/70 leading-relaxed mb-3">{para}</p>
          ))}
        </section>
      )}

      {/* 7. Public concerné */}
      {(p.audience || []).length > 0 && (
        <section className="mt-8 card-surface p-6" data-testid="partner-audience">
          <h2 className="font-display text-lg mb-3">Cette rubrique peut notamment aider</h2>
          <ul className="space-y-2">
            {p.audience.map((a, i) => (
              <li key={i} className="text-white/75 text-[15px] flex gap-2.5"><span className="text-gold mt-1.5 shrink-0">•</span>{a}</li>
            ))}
          </ul>
        </section>
      )}

      {/* 7a. Membres (ex. avocats du cabinet) — même fiche */}
      {(p.members || []).length > 0 && (
        <section className="mt-8" data-testid="partner-members">
          <h2 className="font-display text-xl mb-4">Les avocats du cabinet</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {p.members.map((m, i) => (
              <div key={i} className="card-surface p-5 flex flex-col items-center text-center" data-testid={`partner-member-${i}`}>
                <div className="w-24 h-24 rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03]">
                  {m.photo_url
                    ? <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover object-top" />
                    : <div className="w-full h-full flex items-center justify-center"><Users className="text-white/20" size={28} /></div>}
                </div>
                <h3 className="font-display text-lg mt-3">{m.name}</h3>
                {m.role && <p className="text-white/55 text-sm mt-1">{m.role}</p>}
                {m.specialties && <p className="text-gold/80 text-xs mt-2">{m.specialties}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 7b. Informations pratiques (visibles uniquement sur la fiche) */}
      {(p.details || []).length > 0 && (
        <section className="mt-8 card-surface p-6" data-testid="partner-details">
          <h2 className="font-display text-lg mb-4">Informations pratiques</h2>
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {p.details.map((d, i) => (
              <div key={i} className="flex flex-col">
                <dt className="text-white/40 text-[11px] uppercase tracking-wide">{d.label}</dt>
                <dd className="text-white/80 text-sm mt-0.5">{d.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {/* 8. Boutons */}
      <div className="grid sm:grid-cols-2 gap-3 mt-8" data-testid="partner-buttons">
        {(p.buttons || []).map((b, i) => {
          const Icon = ICONS[b.icon] || ExternalLink;
          const primary = b.kind === "primary";
          return (
            <a key={i} href={b.url} target="_blank" rel="noopener noreferrer" data-testid={`partner-btn-${i}`}
              className={`rounded-full px-5 py-3.5 text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors ${primary ? "gold-gradient text-black font-semibold hover:opacity-90" : "border border-white/20 text-white hover:border-gold/40 hover:text-gold"}`}>
              <Icon size={16} /> {b.label}
            </a>
          );
        })}
      </div>

      {/* 9. Encadré d'information */}
      {p.disclaimer && (
        <div className="mt-10 rounded-2xl bg-white/[0.03] border border-white/10 p-5" data-testid="partner-disclaimer">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={16} className="text-white/50" />
            <p className="text-white/70 text-sm font-medium">À propos des professionnels</p>
          </div>
          <p className="text-white/45 text-sm leading-relaxed">{p.disclaimer}</p>
        </div>
      )}
    </Container>
  );
}
